import React, { useState, useEffect } from 'react';
import './BettingControls.css';

const fmt = (n) => n >= 1000 ? `$${(n / 1000).toFixed(1)}k` : `$${n.toFixed(2).replace(/\.00$/, '')}`;

export default function BettingControls({ actions, onAction, playerName, stack }) {
  const {
    canCheck, canFold, canCall, canRaise, callAmount, raiseCapped,
    minRaise, maxRaise, fixedRaiseAmount,
    isLimit, isSpreadLimit, isPotLimit,
    halfPot, threeQPot, fullPot, twoPot,
    handCap, committed, capRemaining,
  } = actions;

  const [raiseAmount, setRaiseAmount] = useState(minRaise ?? 0);

  // Reset slider when it becomes our turn or limits change
  useEffect(() => {
    setRaiseAmount(minRaise ?? 0);
  }, [actions.seat, minRaise]);

  // ── Preset buttons for PL / NL ──
  const potSizes = isPotLimit
    ? [
        { label: 'Min',   amount: minRaise },
        { label: '½ Pot', amount: halfPot },
        { label: '¾ Pot', amount: threeQPot },
        { label: 'Pot',   amount: maxRaise },
      ]
    : [
        { label: 'Min',    amount: minRaise },
        { label: '½ Pot',  amount: halfPot },
        { label: 'Pot',    amount: fullPot },
        { label: '2× Pot', amount: twoPot },
        { label: 'All-In', amount: maxRaise },
      ];
  const dedupedSizes = potSizes.filter((v, i, arr) => i === 0 || v.amount > arr[i - 1].amount);

  const handleRaise = () => onAction({ type: 'RAISE', seat: actions.seat, amount: raiseAmount });
  const handleFixedRaise = () => onAction({ type: 'RAISE', seat: actions.seat, amount: fixedRaiseAmount });

  return (
    <div className="betting-controls">
      <div className="betting-meta">
        <span className="betting-player">{playerName}</span>
        <span className="betting-stack money">Stack: {fmt(stack)}</span>
        <span className={`betting-prompt ${raiseCapped ? 'capped' : ''}`}>
          {raiseCapped ? 'Betting capped' : 'Your action'}
        </span>
      </div>

      {handCap != null && committed != null && (
        <div className="cap-row">
          <div className="cap-bar-track">
            <div
              className="cap-bar-fill"
              style={{ width: `${Math.min(100, (committed / handCap) * 100)}%` }}
            />
          </div>
          <span className="cap-label money">
            {fmt(committed)} committed, <strong>{fmt(capRemaining ?? 0)} left</strong> of {fmt(handCap)}
          </span>
        </div>
      )}

      {/* Slider raise row — PL, NL, Spread-Limit */}
      {canRaise && !isLimit && (
        <div className="raise-row">
          {/* Presets — PL and NL only */}
          {!isSpreadLimit && (
            <div className="raise-presets">
              {dedupedSizes.map(ps => (
                <button
                  key={ps.label}
                  className={`preset-btn ${raiseAmount === ps.amount ? 'active' : ''}`}
                  onClick={() => setRaiseAmount(ps.amount)}
                >
                  {ps.label}
                </button>
              ))}
            </div>
          )}
          <div className="raise-slider-row">
            <input
              type="range"
              className="raise-slider"
              min={minRaise}
              max={maxRaise}
              step={0.01}
              value={raiseAmount}
              onChange={(e) => setRaiseAmount(Number(e.target.value))}
            />
            <span className="raise-amount-display money">{fmt(raiseAmount)}</span>
          </div>
        </div>
      )}

      <div className="action-buttons">
        {canFold && (
          <button className="action-btn fold" onClick={() => onAction({ type: 'FOLD', seat: actions.seat })}>
            Fold
          </button>
        )}
        {(canCheck || canCall) && (
          <button
            className={`action-btn check ${canCall ? 'calling' : ''}`}
            onClick={() => onAction(canCall
              ? { type: 'CALL', seat: actions.seat }
              : { type: 'CHECK', seat: actions.seat }
            )}
          >
            {canCall ? `Call ${fmt(callAmount)}` : 'Check'}
          </button>
        )}
        {/* Limit: fixed raise button inline with other actions */}
        {canRaise && isLimit && (
          <button className="action-btn raise money" onClick={handleFixedRaise}>
            Raise {fmt(fixedRaiseAmount)}
          </button>
        )}
        {/* PL / NL / Spread-Limit: raise button reads slider */}
        {canRaise && !isLimit && (() => {
          // Rouge is reserved for irreversible moves, and this is the only
          // one a player can choose.
          const isAllIn = !isPotLimit && raiseAmount >= maxRaise;
          return (
            <button
              className={`action-btn raise money ${isAllIn ? 'all-in' : ''}`}
              onClick={handleRaise}
            >
              {isAllIn ? 'All in' : 'Raise'} {fmt(raiseAmount)}
            </button>
          );
        })()}
      </div>
    </div>
  );
}
